import { faArrowDown } from "@fortawesome/pro-solid-svg-icons/faArrowDown";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, CircularProgress, Paper, SxProps, TextField, Theme, Typography } from '@mui/material';
import Autocomplete, { AutocompleteChangeReason, AutocompleteProps } from '@mui/material/Autocomplete';
import { SEARCH_DELAY_DEBOUNCE } from "app/appConstants";
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { GenericAbortSignal } from 'axios';
import { MprcChip } from "components/MprcChip";
import { useMprcTheme } from 'components/MprcThemeProvider';
import { useSimpleDebounce } from "hooks/useDebounce";
import { useReferredState } from "hooks/useReferredState";
import { assocRefToPropRef } from "lib/ref";
import { isArray } from "lodash";
import { isCanceledError } from 'providers/CoreApiProvider';
import React, { useEffect, useRef, useState } from 'react';
import { AutoCompleteDataModel, AutoCompleteSuggestions, createAutoCompleteModel } from './AutoCompleteDataModel';

////////////////////////////////////////////////////////////////////////////

export interface IAutoCompleteProps {
  id?: string;
  label?: string;
  placeholder?: string;
  maxSuggestionCount?: number;
  maxSelectItems?: number;
  multiSelect?: boolean;
  singleSelectMode?: "chip" | "text";
  required?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  freeSolo?: boolean;
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  error?: boolean;
  helperText?: React.ReactNode;
  selectedItems: AutoCompleteDataModel[];
  emptyThumbnail?: string;
  startInputValue?: string;
  inputRef?: React.Ref<any>;
  disabledInput?: boolean;
  margin?: MuiMargin;
  onGetSuggestions: (searchText: string, pageNumber: number, pageSize: number, trySelectedHistory: boolean, signal: GenericAbortSignal | undefined) => Promise<AutoCompleteSuggestions<AutoCompleteDataModel>>;
  onChange: (item: AutoCompleteDataModel[]) => void;
  onKeyReturn?: (value: string) => void;
  onDisplayLabel?: (item: AutoCompleteDataModel) => string;
  onDisplayThumbnail?: (item: AutoCompleteDataModel) => string;
  onInputValueChange?: (value: string) => void;
  // Ao pressionar enter após selecionar um item.
  onApply?: () => void;
  // Ao pressionar ESC quando nenhum item esta selecionado
  onCancel?: () => void;
  onError?: (error: any) => void;
  sx?: SxProps<Theme>;
}

type Props<T> = IAutoCompleteProps &
  Omit<AutocompleteProps<T, boolean, boolean, boolean>,
    "value" | "options" | "renderInput" | "onChange" | "loading" | "isOptionEqualToValue" | "renderTags" | "helperText" | "multiple" | "disabled">;

const KEY_MORE_ITEMS = "key_more_items";

////////////////////////////////////////////////////////////////////////////

/**
 * Componente autocomplete baseado no MUI
 * @param props
 */
export const MprcAutoComplete: React.FC<Props<AutoCompleteDataModel>> = (props) => {

  const {
    selectedItems,
    maxSelectItems,
    label,
    placeholder,
    maxSuggestionCount = 30,
    color,
    error,
    helperText,
    multiSelect = true,
    inputRef,
    disabledInput,
    onChange,
    onKeyReturn,
    onApply,
    onCancel,
    onGetSuggestions,
    onDisplayLabel,
    required = false,
    startAdornment,
    endAdornment,
    emptyThumbnail,
    onDisplayThumbnail,
    autoFocus,
    onInputValueChange,
    startInputValue,
    disabled = false,
    freeSolo = false,
    singleSelectMode = "chip",
    margin,
    sx,
    id = new Date().getTime().toString(),
  } = props;

  const { theme } = useMprcTheme();

  const [loading, setLoading, loadingRef] = useReferredState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<AutoCompleteDataModel[]>([]);
  const [inputValue, setInputValue] = useState<string>(startInputValue ?? "");

  const isMoreItems = useRef<boolean>(false);
  const lastItemSelectd = useRef<string>("");
  const reasonHighlight = useRef<string>("");
  const abortControllerRef = useRef<AbortController>(undefined);
  const localInputRef = React.useRef<HTMLInputElement | null>(null);

  const creatingNewOption = React.useRef<boolean>(false);

  const newSearchText = useSimpleDebounce(inputValue, SEARCH_DELAY_DEBOUNCE);

  /**
   * Verificar se tem histórico de seleção.
   * @deprecated Recurso desabilitado
   */
  const trySelectionHistory = useRef<boolean>(false);

  const singleSelectAsText = singleSelectMode == "text";

  ////////////////////////////////////////////////////////////////////////////
  /// HANDLES
  ////////////////////////////////////////////////////////////////////////////

  function tryClearInput() {
    if (!singleSelectAsText) setInputValue("");
  }

  // Verifica se a tecla pressionada é o [Enter] e se não exitir sugestões chama evento
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {

    if (multiSelect && !!maxSelectItems && maxSelectItems > 0 && !!selectedItems && selectedItems.length == maxSelectItems && event.keyCode != 8) {
      event.preventDefault();
      return;
    }

    switch (event.key) {
      case 'ArrowDown':

        if (suggestions.length > 0) {
          event.preventDefault();

          const selected = suggestions[0];

          // Se a lista é de histórico, limpa e carrega os dados da raiz
          if (selected.fromHistory) {
            trySelectionHistory.current = false;
            isMoreItems.current = false;
            doSearch();
          }
        }
        break;

      case "Enter":
        event.preventDefault();

        if (onKeyReturn && (suggestions.length == 0 || suggestions.find(x => x.label.toLowerCase() == inputValue.toLowerCase()) == undefined)) {
          creatingNewOption.current = true;
          onKeyReturn(inputValue);
          tryClearInput();
        }
        // Opção para aplicar em pesquisa, segundo enter quando tem itens selecionado e não tem sugestões.
        else if (onApply && inputValue == "" && selectedItems.length > 0 && suggestions.length == 0) {
          onApply();
        }
        break;

      case "Escape":

        if (onCancel && selectedItems.length == 0 && suggestions.length == 0) {
          onCancel();
        }
        break;
    }

    isMoreItems.current = false;
  };


  // Verifica se pode mudar as propriedades e chama o onChange
  const handleChange = (e: React.SyntheticEvent, valueReceived: AutoCompleteDataModel[] | AutoCompleteDataModel, reason: AutocompleteChangeReason) => {

    if (loadingRef.current || creatingNewOption.current)
      return;

    if (valueReceived == null) {
      if (singleSelectAsText) {
        onChange([]);
        setInputValue("");
      }
    }
    else {
      var value = isArray(valueReceived) ? valueReceived : [valueReceived];

      // Não permite excluir itens que não podem ser excluídos ao pressionar backspace
      if (reason == "removeOption" && e.type === "keydown" && selectedItems && selectedItems.length > 0 && !selectedItems[0].canDelete)
        return;

      const lastItem = value && value.length > 0 ? value[value.length - 1] : null;
      lastItemSelectd.current = lastItem ? lastItem.key : "";

      // Caso seja para carregar mais items, aciona effect
      if (lastItemSelectd.current == KEY_MORE_ITEMS) {
        e.preventDefault();
        isMoreItems.current = true;
        doSearch();
        return;
      }

      if (multiSelect || (!multiSelect && selectedItems?.length == 0))
        onChange(value);
      else
        onChange(lastItem ? [lastItem] : []);

      if (singleSelectAsText)
        setInputValue(lastItem?.label ?? "");

      tryClearInput();
    }
  };

  // Exclui o item pelo icone excluir do Chip
  const handleDeleteChip = (item: AutoCompleteDataModel | null) => {
    if (selectedItems && item) {
      const value = selectedItems.filter(x => x != item);
      onChange(value);
    }
  };

  ////////////////////////////////////////////////////////////////////////////
  /// EFFECT's
  ////////////////////////////////////////////////////////////////////////////

  async function doSearch() {

    if (!open) return;

    const offset = isMoreItems.current ? suggestions.length : 0;
    const pageNumber = Math.trunc(offset / maxSuggestionCount!) + 1;

    setLoading(true);
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    try {
      var data = await onGetSuggestions(newSearchText, pageNumber, maxSuggestionCount!, false, abortControllerRef.current.signal);
      let totalRows = data.items.length;
      let newSuggestions = data.items;

      // Incrementa o array em outro e remove a opção de mais itens
      if (isMoreItems.current) {
        totalRows += suggestions.length;
        newSuggestions = [...suggestions.filter(x => x.key != KEY_MORE_ITEMS), ...newSuggestions];
      }

      // Inclui a opção de mais itens, se tiver mais linhas para carregar
      if (data.totalRows > totalRows && maxSuggestionCount && maxSuggestionCount > 0) {
        newSuggestions.push(createAutoCompleteModel(
          KEY_MORE_ITEMS,
          theme.MprcAutoComplete2.loadMoreText,
          undefined,
          false
        ));
      }

      setSuggestions(newSuggestions);
    }
    catch (e) {
      if (!isCanceledError(e)) {
        setSuggestions([]);
      }
    }
    finally {
      setLoading(false);
      abortControllerRef.current = undefined;
    }
  }

  /// Executa a pesquisa depois do tempo padrão.
  useEffect(() => {
    if (onInputValueChange) onInputValueChange(newSearchText);
    doSearch();
  }, [newSearchText]);

  const handleInputRef = (ref: HTMLInputElement | null): void => {
    localInputRef.current = ref;

    if (inputRef) {
      assocRefToPropRef(ref, localInputRef);
    }
  };

  // Atualiza quando a caixa de items é aberta ou fechada.
  useEffect(() => {
    if (open) doSearch();
    else {
      setSuggestions([]);
      isMoreItems.current = false;
      lastItemSelectd.current = "";
    }
  }, [open]);

  useEffect(() => {
    if (autoFocus && localInputRef) (localInputRef as any)?.current?.focus();
  }, [localInputRef]);

  useEffect(() => {
    if (singleSelectAsText && selectedItems.length > 0) setInputValue(selectedItems.length > 0 ? selectedItems[0].label : "");
    creatingNewOption.current = false;
  }, [selectedItems]);

  ////////////////////////////////////////////////////////////////////////////
  /// RENDER
  ////////////////////////////////////////////////////////////////////////////

  return (
    <Autocomplete
      id={id}
      autoFocus={autoFocus}
      fullWidth
      disableClearable={!singleSelectAsText || inputValue == ""}
      multiple={!singleSelectAsText}
      disabled={disabled}
      openText=""
      open={open}
      clearOnEscape
      autoHighlight
      freeSolo={freeSolo}
      onOpen={() => {
        setOpen(
          disabledInput
            ? false
            : multiSelect
              ? maxSelectItems == undefined || (!!maxSelectItems && maxSelectItems > 0 && !!selectedItems && selectedItems.length < maxSelectItems)
              : true
        );
      }}
      onClose={() => {
        if (lastItemSelectd.current != KEY_MORE_ITEMS)
          setOpen(false);
        lastItemSelectd.current = "";
      }}
      getOptionDisabled={(option) => option.selectable === false}
      loadingText={theme.MprcAutoComplete2.loadingText}
      options={suggestions}
      loading={loading}
      getOptionLabel={(option) => typeof option === "string" ? option : option.label ?? ""}
      inputValue={inputValue}
      value={selectedItems}
      onChange={handleChange}
      isOptionEqualToValue={(option, value) => option && value && option.key === value.key}
      filterSelectedOptions={multiSelect}
      filterOptions={(options) => options}
      noOptionsText={theme.MprcAutoComplete2.noOptionsText}
      renderInput={(params) => (
        <TextField
          {...params}
          type="text"
          disabled={disabledInput}
          error={error}
          helperText={helperText}
          variant="standard"
          label={label}
          color={color}
          margin={margin}
          placeholder={placeholder}
          onBlur={() => {
            if (!freeSolo && suggestions && suggestions.length == 0) tryClearInput();
          }}
          onChange={(e) => {
            if (disabledInput) return;
            setInputValue(e.target.value);
          }}
          onKeyDown={handleKeyDown}
          required={required}
          fullWidth
          slotProps={{
            htmlInput: {
              ...params.inputProps,
              autoComplete: "do-not-autofill",
            },
            input: {
              ...params.InputProps,
              style: { paddingRight: endAdornment ? 0 : undefined },
              startAdornment: startAdornment || params.InputProps.startAdornment,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {endAdornment || params.InputProps.endAdornment}
                </>
              )
            }
          }}
          autoCorrect="off"
          sx={sx}
          inputRef={handleInputRef}
        />
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          if (option) {
            const avatarUrl = option.thumbnailUrl != null
              ? option.thumbnailUrl
              : onDisplayThumbnail
                ? onDisplayThumbnail(option)
                : emptyThumbnail;

            const avatar = avatarUrl
              ? <Avatar variant="circular" src={avatarUrl} sx={{ width: 24, height: 24 }} />
              : undefined;

            return (
              <MprcChip
                {...getTagProps({ index })}
                key={`chip-${index}`}
                label={onDisplayLabel ? onDisplayLabel(option) : option.label}
                size="small"
                avatar={avatar}
                onDelete={!option.canDelete ? undefined : () => handleDeleteChip(option)}
              />);
          }
          return null;
        })
      }
      PaperComponent={({ children }) => (
        <Paper elevation={2}>
          <>
            {suggestions && suggestions.length > 0 && suggestions[0].fromHistory &&
              <Typography marginLeft={2} component="div" noWrap={true} variant="caption">
                <span>
                  {theme.MprcAutoComplete.displayingHistory}
                  <FontAwesomeIcon icon={faArrowDown} />
                  {theme.MprcAutoComplete.exitFromHistory}
                </span>
              </Typography>
            }
            {children}
          </>
        </Paper>
      )}
      onHighlightChange={(event: React.SyntheticEvent, option: any, reason: string) => {
        reasonHighlight.current = reason;
      }}
      renderOption={(props, option, { inputValue }) => {

        if (option.key == KEY_MORE_ITEMS) {
          return <li {...props}><b>{option.label}</b></li>;
        }

        const matches = match(option.label, inputValue);
        const parts = parse(option.label, matches);

        const avatarUrl = option.thumbnailUrl != null
          ? option.thumbnailUrl
          : onDisplayThumbnail
            ? onDisplayThumbnail(option)
            : emptyThumbnail;

        return (
          <li {...props} key={option.key}>
            <>
              {
                avatarUrl &&
                <Avatar sx={{ width: 24, height: 24, marginRight: 1 }} src={avatarUrl} />
              }
              <div>
                {
                  parts.map((part, index) => (
                    <React.Fragment key={index}>
                      <span
                        key={index}
                        style={{
                          fontWeight: part.highlight ? 700 : 400,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {part.text}
                      </span>
                    </React.Fragment>
                  ))
                }
              </div>
            </>
          </li>
        );
      }}
    />
  );
};
